import { KYIV_BOUNDS, LANDMARK_KINDS, type Bounds, type BindingInput, type LandmarkInput } from "./types";

export class NavigationError extends Error {
    constructor(message: string, public status = 400) {
        super(message);
    }
}

function object(value: unknown): Record<string, unknown> {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new NavigationError("Очікується JSON-об’єкт.");
    }
    return value as Record<string, unknown>;
}

function text(value: unknown, name: string, max: number): string {
    if (typeof value !== "string" || value.trim().length > max) {
        throw new NavigationError(`${name}: допустимо до ${max} символів.`);
    }
    return value.trim();
}

function number(value: unknown, name: string, min: number, max: number): number {
    if (typeof value !== "number" || !Number.isFinite(value) || value < min || value > max) {
        throw new NavigationError(`${name}: введіть число від ${min} до ${max}.`);
    }
    return value;
}

export function validateCoordinates(longitude: unknown, latitude: unknown): [number, number] {
    return [
        number(longitude, "Довгота", KYIV_BOUNDS[0], KYIV_BOUNDS[2]),
        number(latitude, "Широта", KYIV_BOUNDS[1], KYIV_BOUNDS[3]),
    ];
}

export function parseBounds(raw: string | null): Bounds {
    if (!raw || raw.split(",").some(part => !part.trim())) {
        throw new NavigationError("Потрібна область bbox: west,south,east,north.");
    }
    const values = raw.split(",").map(Number);
    if (values.length !== 4 || values.some(value => !Number.isFinite(value))) {
        throw new NavigationError("Некоректна область карти.");
    }
    const [west, south, east, north] = values;
    if (west < -180 || east > 180 || south < -90 || north > 90 || west >= east || south >= north) {
        throw new NavigationError("Некоректні межі області карти.");
    }
    return [west, south, east, north];
}

export function validateVersion(value: unknown): number {
    const version = number(value, "Версія", 1, 2147483647);
    if (!Number.isInteger(version)) throw new NavigationError("Некоректна версія запису.");
    return version;
}

export function validateId(value: string): string {
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
        throw new NavigationError("Некоректний ID орієнтира.");
    }
    return value;
}

export function validateLandmark(value: unknown, updating = false): LandmarkInput {
    const data = object(value);
    if (!LANDMARK_KINDS.includes(data.kind as LandmarkInput["kind"])) {
        throw new NavigationError("Виберіть тип орієнтира.");
    }
    const kind = data.kind as LandmarkInput["kind"];
    const signCode = text(data.signCode, "Код знака", 40);
    if (kind === "road_sign" && !signCode) throw new NavigationError("Укажіть код дорожнього знака.");
    if (typeof data.verified !== "boolean") throw new NavigationError("Укажіть статус перевірки.");
    if (!Array.isArray(data.bindings) || data.bindings.length > 6) {
        throw new NavigationError("Можна прив’язати до 6 дорожніх ділянок.");
    }
    const bindings: BindingInput[] = data.bindings.map(value => {
        const binding = object(value);
        const edgeId = text(binding.edgeId, "Ділянка", 100);
        if (!/^\d+:\d+:\d+$/.test(edgeId)) throw new NavigationError("Некоректна дорожня ділянка.");
        if (!["forward", "backward", "both"].includes(binding.direction as string)) {
            throw new NavigationError("Виберіть напрямок руху.");
        }
        return { edgeId, direction: binding.direction as BindingInput["direction"] };
    });
    if (new Set(bindings.map(binding => binding.edgeId)).size !== bindings.length) {
        throw new NavigationError("Ділянки у прив’язках не повинні повторюватись.");
    }
    const [longitude, latitude] = validateCoordinates(data.longitude, data.latitude);
    return {
        kind, longitude, latitude, bindings, signCode: kind === "road_sign" ? signCode : "",
        signValue: kind === "road_sign" ? text(data.signValue, "Значення знака", 60) : "",
        title: text(data.title, "Назва", 160),
        notes: text(data.notes, "Нотатки", 2000),
        viewHeading: data.viewHeading === null ? null : number(data.viewHeading, "Азимут", 0, 359.99),
        accuracyMeters: data.accuracyMeters === null ? null : number(data.accuracyMeters, "Похибка координат", 0.1, 1000),
        verified: data.verified,
        ...(updating ? { version: validateVersion(data.version) } : {}),
    };
}
